import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "@/i18n/translations";
import { API } from "@/lib/api";

const LanguageContext = createContext(null);

const get = (obj, path) => {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("orion-lang") || "en";
    }
    return "en";
  });

  // Runtime overrides fetched from /api/content — admin-editable.
  // Shape: { [lang]: { [dottedKey]: value } }
  const [overrides, setOverrides] = useState({ en: {}, ar: {} });

  const dir = lang === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", dir);
    document.body.setAttribute("dir", dir);
    if (lang === "ar") {
      document.body.classList.add("font-arabic");
    } else {
      document.body.classList.remove("font-arabic");
    }
  }, [lang, dir]);

  // Fetch content blocks once on mount
  const loadOverrides = useCallback(async () => {
    try {
      const res = await fetch(`${API}/content`);
      if (!res.ok) return;
      const data = await res.json();
      const map = { en: {}, ar: {} };
      data.forEach((b) => {
        if (!b || !b.key || !b.value) return;
        const l = b.lang === "ar" ? "ar" : "en";
        map[l][b.key] = b.value;
      });
      setOverrides(map);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Failed to load content overrides", err);
      }
    }
  }, []);

  useEffect(() => {
    loadOverrides();
    // Refresh overrides when the admin panel writes an update
    const handler = () => loadOverrides();
    window.addEventListener("orion-content-updated", handler);
    return () => window.removeEventListener("orion-content-updated", handler);
  }, [loadOverrides]);

  const setLang = useCallback((next) => {
    setLangState(next);
    try {
      localStorage.setItem("orion-lang", next);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("orion-lang localStorage write failed", err);
      }
    }
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === "en" ? "ar" : "en");
  }, [lang, setLang]);

  const t = useCallback(
    (path, fallback = "") => {
      // 1. Admin-editable runtime override wins
      const override = overrides[lang] && overrides[lang][path];
      if (override !== undefined && override !== "") return override;
      // 2. Translation dictionary for current language
      const value = get(translations[lang], path);
      if (value !== undefined) return value;
      // 3. English fallback (same path)
      const enOverride = overrides.en && overrides.en[path];
      if (enOverride !== undefined && enOverride !== "") return enOverride;
      const enValue = get(translations.en, path);
      return enValue !== undefined ? enValue : fallback;
    },
    [lang, overrides]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t, dir, reloadOverrides: loadOverrides }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be inside LanguageProvider");
  return ctx;
};
